import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { dataService } from '../../../Services/data/data.service';
import { useTranslation } from 'react-i18next';

import { makeStyles } from "@mui/styles";
import FlagstoneTheme from '../../../Config/flagstoneTheme';

import { roles } from '../../../Config/constants';

import { Eye, EyeOff, LoaderCircle } from 'lucide-react';

export const LoginPage = (props) => {
    const navigate = useNavigate();
    const location = useLocation();

    const classes = useStyles();
    const { t } = useTranslation();

    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");

    const [passwordVisible, setPasswordVisible] = useState(false);

    const [nameError, setNameError] = useState(false);
    const [passwordError, setPasswordError] = useState(false);

    const [submitting, setSubmitting] = useState(false);
    const [status, setStatus] = useState(null);

    // Get the path the user was trying to access before login
    const from = location.state?.from?.pathname || '/'; // Default to home page if no path

    const _handleKeyDownSubmitLoginForm = (e) => {
        if (e.key === 'Enter') {
            submitLoginForm();
        }
    }

    const submitLoginForm = () => {
        if (submitting) return;
        setSubmitting(true);
        setStatus(null);

        if (validateLoginForm()) {
            dataService.authenticate(username, password)
                .then(
                    loginResult => {
                        let parsedUser = JSON.parse(JSON.parse(loginResult.data.Messages[0]).Data).user;
                        let parsedMessage = JSON.parse(JSON.parse(loginResult.data.Messages[0]).Data).message;

                        if (parsedUser != null && parsedUser != undefined) {
                            if (parsedUser.Roles && parsedUser.Roles.some(role => role.Id === roles.ADMIN_ROLE)) {
                                props.actions.login(parsedUser);

                                navigate(from); // After successful login, redirect back to the original path
                            } else {
                                props.actions.logout();
                                setStatus(t("You're not authorized to use the application"));
                                setSubmitting(false);
                            }
                        } else {
                            // Show feedback api
                            // TODO
                            props.actions.logout();
                            setStatus(t(parsedMessage));
                            setSubmitting(false);
                        }
                    },
                    error => {
                        setStatus(error.message);
                        setSubmitting(false);
                    }
                );
        } else {
            setSubmitting(false);
        }
    };

    const validateLoginForm = () => {
        let isValid = true;

        if (!username) {
            setNameError(t("Username is required"));
            isValid = false;
        } else {
            setNameError(null);
        }

        if (!password) {
            setPasswordError(t("Password is required"));
            isValid = false;
        } else {
            setPasswordError(null);
        }

        return isValid;
    }

    return (
        <div className={classes.card}>
            <div className={classes.card__heading} >
                <h1 className={classes.card__title}>{t("Login")}</h1>
                {status ?
                    <p className={classes.card__subtitle} style={{ color: "#D84949" }}>{status}</p>
                    :
                    <p className={classes.card__subtitle}>{t("Please authenticate to gain access")}</p>
                }
            </div>

            <div className={classes.card__form}>
                <div className={classes.form__row}>
                    <div className={classes.form__inputContainer}>
                        <label className={classes.form__label} style={{ color: nameError ? "#D84949" : FlagstoneTheme.palette.text.primary }}>{nameError ? nameError : t("Username")}</label>
                        <input className={classes.form__input} style={{ borderColor: nameError ? "#D84949" : "#A9A9A9" }} type="text" value={username} onChange={(e) => setUsername(e.target.value)} />
                    </div>
                </div>
                <div className={classes.form__row}>
                    <div className={classes.form__inputContainer}>
                        <label className={classes.form__label} style={{ color: passwordError ? "#D84949" : FlagstoneTheme.palette.text.primary }}>{passwordError ? passwordError : t("Password")}</label>
                        <div className={classes.form__passwordInputContainer}>
                            <input className={classes.form__passwordInput} style={{ borderColor: passwordError ? "#D84949" : "#A9A9A9" }} type={passwordVisible ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => _handleKeyDownSubmitLoginForm(e)} />
                            <button onClick={() => setPasswordVisible(!passwordVisible)} className={classes.form__showPasswordButton}>
                                {passwordVisible ?
                                    <EyeOff size={20} strokeWidth={1.4} color={FlagstoneTheme.palette.text.primary} />
                                    :
                                    <Eye size={20} strokeWidth={1.4} color={FlagstoneTheme.palette.text.primary} />
                                }
                            </button>
                        </div>
                    </div>
                </div>
                <div className={classes.form__actions}>
                    <button className={`${classes.form__button} ${submitting ? classes.form__button_loading : ''}`} onClick={() => submitLoginForm()}>
                        {submitting ? <LoaderCircle className={classes.button__loader} /> : t("Login")}
                    </button>
                </div>
            </div>
        </div>
    )
};

const useStyles = makeStyles(() => ({
    card: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'left',
        width: "400px",

        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",

        backgroundColor: "#fff",
        borderRadius: "6px",

        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
        padding: "16px",

        "@media (max-width: 768px)": {
            width: "100%",
        }
    },

    card__heading: {
        display: 'flex',
        flexDirection: 'column',

        gap: '8px',

        padding: '0px',
        margin: '0 0 16px 0',
    },

    card__title: {
        fontSize: '24px',
        fontFamily: "Lato, sans-serift",
        fontWeight: 'normal',

        color: FlagstoneTheme.palette.text.primary,

        padding: '0px',
        margin: '0px',
    },

    card__subtitle: {
        fontSize: '16px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',

        color: FlagstoneTheme.palette.text.secondary,

        margin: '0px',
    },

    card__form: {
        width: "100%",

        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },

    form__row: {
        width: "100%",
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },

    form__label: {
        fontSize: '12px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',
        color: FlagstoneTheme.palette.text.primary,
    },

    form__inputContainer: {
        width: '100%',

        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },

    form__input: {
        width: '100%',
        height: '40px',
        borderRadius: '3px',
        border: '1px solid #A9A9A9',

        padding: '0px 8px',
        paddingLeft: '8px',
        boxSizing: 'border-box',

        fontSize: '16px',
        fontFamily: 'Lato, sans-serif !important',
        fontWeight: 'regular',
        color: '#0C0E29',

        "&:focus": {
            outline: "none",
            border: "1px solid #4654FB",
        },
    },



    form__passwordInputContainer: {
        position: 'relative',
    },

    form__inputFieldContainer: {
        position: 'relative',
    },

    form__passwordInput: {
        width: '100%',
        height: '40px',
        borderRadius: '3px',
        border: '1px solid #A9A9A9',

        padding: '0px 8px',
        paddingLeft: '8px',
        boxSizing: 'border-box',

        fontSize: '16px',
        fontFamily: 'Lato, sans-serif !important',
        fontWeight: 'regular',
        color: '#0C0E29',

        "&:focus": {
            outline: "none",
            border: "1px solid #4654FB",
        },
    },

    form__showPasswordButton: {
        height: "32px",
        width: "32px",
        borderRadius: "4px",

        backgroundColor: "transparent",

        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',

        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        right: '4px',

        cursor: 'pointer',
        border: 'none',

        transition: 'all 0.2s',

        '&:hover': {
            backgroundColor: '#EDEDED',
        },
    },

    form__inputEndAdornment: {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        right: '8px',
    },

    form__actions: {
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '8px',

        marginTop: '8px',
    },

    form__button: {
        color: "white !important",
        fontSize: "16px !important",
        fontWeight: "normal !important",
        fontFamily: "Lato, sans-serif !important",
        backgroundColor: FlagstoneTheme.palette.primary.main + " !important",
        border: "none",
        minWidth: "fit-content !important",
        width: "100%",
        height: '40px !important',
        padding: '0px 20px !important',
        textTransform: "none !important",
        borderRadius: "3px !important",

        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',

        // align right
        marginLeft: 'auto !important',

        transition: 'all 0.2s !important',

        '&:hover': {
            backgroundColor: "#3B3AF1 !important",
            color: "white !important",
            cursor: "pointer !important",
        },
        '&:disabled': {
            backgroundColor: "lightgrey !important",
            color: "white !important",
            border: "1px solid darkgrey !important",
            cursor: "not-allowed !important"
        },
    },

    form__button_loading: {
        backgroundColor: "#C3D3FF !important",
        color: FlagstoneTheme.palette.primary.main + " !important",

        '&:hover': {
            backgroundColor: "#C3D3FF !important",
            color: FlagstoneTheme.palette.primary.main + " !important",
            cursor: "default !important",
        },
    },

    button__loader: {
        width: "24px",
        height: "24px",
        color: FlagstoneTheme.palette.primary.main,

        animation: "spin 1s linear infinite",
    },

    '@global': {
        '@keyframes spin': {
            '0%': {
                transform: 'rotate(0deg)',
            },
            '100%': {
                transform: 'rotate(360deg)',
            },
        },
    }
}));

export default LoginPage;
